import React, { useState, useEffect, useMemo } from 'react'
import CustomSelect from '../commonSelect';
import { AgeOption, CountryOption } from '../selectOption/json/selectOption';
import { Link, useLocation } from 'react-router-dom';
import ImageWithBasePath from '../imageWithBasePath';
import PaymentModal from '../../../feature-module/tour/tour-booking/paymentSuccessfulModal';
import { all_routes } from '../../../feature-module/router/all_routes';
import { DatePicker, Tooltip } from 'antd';
import axios from 'axios';
import dayjs, { Dayjs } from "dayjs";

type PasswordField = "password" | "confirmPassword";

type VeiculoDTO = {
    veiculoId: number;
    modelo: string;
    placa: string;
    capacidade: number;
    fotoUrl?: string;
};

type MotoristaDTO = {
    id: number;
    nome: string;
    email: string;
    telefone?: string;
    descricao?: string;
    idiomas?: string[];
    fotoId?: number;
    ativo?: boolean;
    veiculo?: VeiculoDTO;
};

type HorarioDTO = {
    inicio: string;
    fim: string;
};

type HorarioDisponivelDTO = {
    data: string; // yyyy-mm-dd
    horarios: HorarioDTO[];
};

interface Reserva {
    motoristaId: number | null;
    data: string | null;
    horario: string | null;
    adultos: number;
    criancas: number;
}

// BookingFormProps
interface BookingFormProps {
    packageId: number | null;
    onReservaChange: (reserva: Reserva) => void;
    idadeMinima?: number | null;
    tourDurationMinutes?: number; // <<< novo
}


const BASE_URL = process.env.REACT_APP_API_URL;

const BookingForm = ({ packageId, onReservaChange, idadeMinima, tourDurationMinutes }: BookingFormProps) => {
    const location = useLocation();
    const [addModal, setAddModal] = useState(false);

    const [motoristas, setMotoristas] = useState<MotoristaDTO[]>([]);
    const [motoristaSelecionado, setMotoristaSelecionado] = useState<number | null>(null);

    const [horariosDisponiveis, setHorariosDisponiveis] = useState<HorarioDisponivelDTO[]>([]);
    const [dataReserva, setDataReserva] = useState<string | null>(null);
    const [horarioSelecionado, setHorarioSelecionado] = useState<string | null>(null);

    // Buscar motoristas do pacote
    useEffect(() => {
        const fetchMotoristas = async () => {
            if (!packageId) return;
            try {
                const pacoteRes = await axios.get(`${BASE_URL}/pacotes/${packageId}`);
                const pacoteData = pacoteRes.data;

                if (pacoteData.motoristaIds && pacoteData.motoristaIds.length > 0) {
                    const responses = await Promise.all(
                        pacoteData.motoristaIds.map((mid: number) =>
                            axios.get(`${BASE_URL}/motorista/${mid}`)
                        )
                    );
                    setMotoristas(responses.map(res => res.data));
                }
            } catch (err) {
                console.error("Erro ao buscar motoristas:", err);
            }
        };
        fetchMotoristas();
    }, [packageId]);

    // Buscar horários disponíveis ao selecionar motorista
    useEffect(() => {
        if (!motoristaSelecionado) return;

        const fetchHorarios = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/motorista/disponibilidade/horarios`, {
                    params: {
                        motoristaId: motoristaSelecionado,
                        duracaoTour: tourDurationMinutes ?? 60, // <- envia para o backend
                    },
                });

                setHorariosDisponiveis(res.data);
                setDataReserva(null);
                setHorarioSelecionado(null);
            } catch (err) {
                console.error("Erro ao buscar horários disponíveis:", err);
            }
        };

        fetchHorarios();
    }, [motoristaSelecionado, tourDurationMinutes]);

    // Quantidade de pessoas
    const [adultos, setAdultos] = useState<number>(1);
    const [criancas, setCriancas] = useState<number>(0);

    // Calcula total de pessoas
    const totalPessoas = adultos + criancas;

    // Busca capacidade do veículo do motorista selecionado
    const capacidadeVeiculo = motoristas.find(m => m.id === motoristaSelecionado)?.veiculo?.capacidade ?? 0;

    // Atualiza reserva no componente pai incluindo quantidade de pessoas
    useEffect(() => {
        onReservaChange({
            motoristaId: motoristaSelecionado,
            data: dataReserva,
            horario: horarioSelecionado,
            adultos,
            criancas
        });
    }, [motoristaSelecionado, dataReserva, horarioSelecionado, adultos, criancas]);

    const [paymentRadio, setPaymentRadio] = useState('credit-card');
    const folderInputRef = React.useRef<HTMLInputElement | null>(null); const handleOpenFolder = () => { if (folderInputRef.current) { folderInputRef.current.click(); } };
    const [passwordVisibility, setPasswordVisibility] = useState({ password: false, confirmPassword: false, });
    const togglePasswordVisibility = (field: PasswordField) => { setPasswordVisibility((prevState) => ({ ...prevState, [field]: !prevState[field], })); };

    // Calcula opções de horários válidos com base na duração do tour
    // Em vez de gerar availableStartOptions
    const availableStartOptions = useMemo(() => {
        if (!dataReserva) return [];
        const day = horariosDisponiveis.find((h) => h.data === dataReserva);
        if (!day) return [];

        return day.horarios.map((h) => ({
            start: h.inicio,
            end: h.fim
        }));
    }, [horariosDisponiveis, dataReserva]);

    return (
        <>
            <div className="card checkout-card">
                <div className="card-header">
                    <h5>Secure Checkout</h5>
                </div>
                <div className="card-body">

                    {/* Selecionar Motorista */}
                    <div className="checkout-title mb-3">
                        <h6>Select a Driver</h6>
                    </div>
                    <div className="checkout-details mb-4">
                        {motoristas.length === 0 ? (
                            <p>No drivers available for this package.</p>
                        ) : (
                            <div className="row">
                                {motoristas.map((motorista) => {
                                    const isInactive = motorista.ativo === false;

                                    return (
                                        <div
                                            key={motorista.id}
                                            className={`col-lg-12 mb-3 p-3 border rounded ${motoristaSelecionado === motorista.id ? 'border-primary' : 'border-light'
                                                }`}
                                            style={{
                                                cursor: isInactive ? 'not-allowed' : 'pointer',
                                                opacity: isInactive ? 0.5 : 1,
                                                backgroundColor: isInactive ? '#f8f9fa' : 'white',
                                            }}
                                            onClick={() => {
                                                if (!isInactive) setMotoristaSelecionado(motorista.id);
                                            }}
                                        >
                                            <div className="d-flex align-items-start">
                                                <div
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            motoristaSelecionado === motorista.id ? 'orange' : 'lightgray',
                                                        marginRight: 12,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                {motorista.veiculo?.fotoUrl && (
                                                    <img
                                                        src={`${BASE_URL}${motorista.veiculo.fotoUrl}`}
                                                        alt={motorista.veiculo.modelo}
                                                        style={{
                                                            width: 100,
                                                            height: 60,
                                                            objectFit: 'cover',
                                                            marginRight: 12,
                                                        }}
                                                    />
                                                )}
                                                <div>
                                                    <h6 className="mb-1">{motorista.nome}</h6>

                                                    {isInactive && (
                                                        <p className="text-danger mb-1 fw-bold">
                                                            Motorista não disponível no momento
                                                        </p>
                                                    )}

                                                    {motorista.descricao && (
                                                        <p className="mb-1">{motorista.descricao}</p>
                                                    )}

                                                    {motorista.idiomas && motorista.idiomas.length > 0 && (
                                                        <p className="mb-1">
                                                            <strong>Languages:</strong>{' '}
                                                            {motorista.idiomas.join(', ')}
                                                        </p>
                                                    )}

                                                    {motorista.veiculo && (
                                                        <div className="mt-2 d-flex align-items-center">
                                                            {motorista.veiculo.fotoUrl && (
                                                                <img
                                                                    src={`${BASE_URL}${motorista.veiculo.fotoUrl}`}
                                                                    alt={motorista.veiculo.modelo}
                                                                    style={{
                                                                        width: 100,
                                                                        height: 60,
                                                                        objectFit: 'cover',
                                                                        marginRight: 12,
                                                                    }}
                                                                />
                                                            )}
                                                            <div>
                                                                <p className="mb-0">
                                                                    <strong>Model:</strong>{' '}
                                                                    {motorista.veiculo.modelo}
                                                                </p>
                                                                <p className="mb-0">
                                                                    <strong>Capacity:</strong>{' '}
                                                                    {motorista.veiculo.capacidade}
                                                                </p>
                                                                <p className="mb-0">
                                                                    <strong>Plate:</strong>{' '}
                                                                    {motorista.veiculo.placa}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Seleção de quantidade de pessoas */}
                    {motoristaSelecionado && capacidadeVeiculo > 1 && (
                        <div className="mt-3">
                            <h6>Passengers (Max: {capacidadeVeiculo - 1})</h6>
                            {idadeMinima !== null && (
                                <div
                                    className="alert alert-warning py-2 px-3 mb-3"
                                    style={{ fontSize: "0.95rem" }}
                                >
                                    ⚠️ Minimum age required: <strong>{idadeMinima} years</strong>
                                </div>
                            )}
                            <div className="d-flex gap-3 align-items-center">

                                {/* Adultos */}
                                <div className="d-flex align-items-center gap-2">
                                    <span>Adults:</span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => setAdultos(prev => Math.max(1, prev - 1))}
                                    >-</button>
                                    <span>{adultos}</span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => {
                                            if (adultos + criancas < capacidadeVeiculo - 1) {
                                                setAdultos(prev => prev + 1);
                                            }
                                        }}
                                    >+</button>
                                </div>

                                {/* Crianças */}
                                <div className="d-flex align-items-center gap-2">
                                    <span>Children:</span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => setCriancas(prev => Math.max(0, prev - 1))}
                                    >-</button>
                                    <span>{criancas}</span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => {
                                            if (adultos + criancas < capacidadeVeiculo - 1) {
                                                setCriancas(prev => prev + 1);
                                            }
                                        }}
                                    >+</button>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Selecionar Data */}
                    <div className="checkout-title mb-2">
                        <h6>Select a Date</h6>
                    </div>
                    <div className="mb-2">
                        <DatePicker
                            value={dataReserva ? dayjs(dataReserva) : null}
                            onChange={(date: Dayjs | null) => {
                                if (date) {
                                    setDataReserva(date.format("YYYY-MM-DD"));
                                    setHorarioSelecionado(null);
                                } else {
                                    setDataReserva(null);
                                }
                            }}
                            disabledDate={(current) => {
                                if (!current) return true;
                                const d = current as Dayjs;
                                const dataStr = d.format("YYYY-MM-DD");
                                return !horariosDisponiveis.some((h) => h.data === dataStr);
                            }}
                            cellRender={(current, info) => {
                                const d = current as Dayjs;
                                const dataStr = d.format("YYYY-MM-DD");
                                const diaDisponivel = horariosDisponiveis.find((h) => h.data === dataStr);

                                if (info.type === "date") {
                                    return (
                                        <Tooltip
                                            title={
                                                diaDisponivel
                                                    ? `${diaDisponivel.horarios.length} slots`
                                                    : "Indisponível"
                                            }
                                        >
                                            <div
                                                style={{
                                                    borderRadius: "50%",
                                                    width: "28px",
                                                    height: "28px",
                                                    lineHeight: "28px",
                                                    margin: "0 auto",
                                                    backgroundColor: diaDisponivel ? "#ffc107" : undefined,
                                                    color: diaDisponivel ? "black" : undefined,
                                                }}
                                            >
                                                {d.date()}
                                            </div>
                                        </Tooltip>
                                    );
                                }
                                return info.originNode;
                            }}
                        />
                    </div>

                    {/* Selecionar Horário */}
                    {dataReserva && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {availableStartOptions.length > 0 ? (
                                availableStartOptions.map((opt, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`btn btn-sm ${horarioSelecionado === opt.start ? "btn-warning" : "btn-light"}`}
                                        onClick={() => setHorarioSelecionado(opt.start)}
                                    >
                                        {opt.start} - {opt.end}
                                    </button>
                                ))
                            ) : (
                                <p className="text-muted">No available times for this date</p>
                            )}
                        </div>
                    )}

                </div>
            </div>

            <div className="card payment-details">
                <div className="card-header">
                    <h5>Payment Details</h5>
                </div>
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
                        <div className="d-flex align-items-center flex-wrap payment-form">
                            <div className="form-check d-flex align-items-center me-3 mb-2">
                                <input
                                    className="form-check-input mt-0"
                                    type="radio"
                                    name="Radio"
                                    id="credit-card"
                                    onChange={() => setPaymentRadio('credit-card')}
                                    checked={paymentRadio === 'credit-card' ? true : false}
                                />
                                <label
                                    className="form-check-label fs-14 ms-2"
                                    htmlFor="credit-card"
                                >
                                    Credit / Debit Card
                                </label>
                            </div>
                            <div className="form-check d-flex align-items-center me-3 mb-2">
                                <input
                                    className="form-check-input mt-0"
                                    type="radio"
                                    name="Radio"
                                    id="paypal"
                                    onChange={() => setPaymentRadio('paypal')}
                                    checked={paymentRadio === 'paypal' ? true : false}
                                />
                                <label
                                    className="form-check-label fs-14 ms-2"
                                    htmlFor="paypal"
                                >
                                    Paypal
                                </label>
                            </div>
                            <div className="form-check d-flex align-items-center me-3 mb-2">
                                <input
                                    className="form-check-input mt-0"
                                    type="radio"
                                    name="Radio"
                                    id="stripe"
                                    onChange={() => setPaymentRadio('stripe')}
                                    checked={paymentRadio === 'stripe' ? true : false}
                                />
                                <label
                                    className="form-check-label fs-14 ms-2"
                                    htmlFor="stripe"
                                >
                                    Stripe
                                </label>
                            </div>
                        </div>
                    </div>
                    {/* Credit Card */}
                    <div className="credit-card-details" style={{ display: paymentRadio === 'credit-card' ? 'block' : 'none' }}>
                        <div className="mb-3">
                            <h6 className="fs-16 ">Payment With Credit Card</h6>
                        </div>
                        <div className="card-detials mb-3">
                            <div className="row g-3">
                                <div className="col-lg-4 d-flex">
                                    <div className="card-content flex-fill">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div>
                                                <ImageWithBasePath
                                                    src="assets/img/icons/visa.svg"
                                                    alt="image"
                                                    className="img-fluid mb-2"
                                                />
                                                <p className="fs-16 fw-medium text-gray-6">
                                                    **** **** **** 2547
                                                </p>
                                            </div>
                                            <div className="card-edit-icon">
                                                <Link
                                                    to="#"
                                                    className="rounded-circle"
                                                >
                                                    {" "}
                                                    <span className=" d-flex align-items-center justify-content-center fs-14">
                                                        <i className="isax isax-edit-2" />
                                                    </span>
                                                </Link>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="fs-14 mb-1">Expiry</h6>
                                            <span className="fs-14 fw-normal text-gray-6">
                                                May 2026
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4 d-flex">
                                    <div className="card-content flex-fill">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div>
                                                <ImageWithBasePath
                                                    src="assets/img/icons/visa-2.svg"
                                                    alt="image"
                                                    className="img-fluid mb-2"
                                                />
                                                <p className="fs-16 fw-medium text-gray-6">
                                                    **** **** **** 2547
                                                </p>
                                            </div>
                                            <div className="card-edit-icon">
                                                <Link
                                                    to="#"
                                                    className="rounded-circle"
                                                >
                                                    {" "}
                                                    <span className=" d-flex align-items-center justify-content-center fs-14">
                                                        <i className="isax isax-edit-2" />
                                                    </span>
                                                </Link>
                                            </div>
                                        </div>
                                        <div>
                                            <h6 className="fs-14 mb-1">Expiry</h6>
                                            <span className="fs-14 fw-normal text-gray-6">
                                                May 2026
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4 d-flex">
                                    <div className="card-add flex-fill d-flex align-items-center justify-content-center">
                                        <div className="add-option d-flex align-items-center justify-content-center">
                                            <Link
                                                to="#"
                                                id="open-folder"
                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                onClick={handleOpenFolder}
                                            >
                                                <span className="d-flex align-items-center justify-content-center">
                                                    <i className="isax isax-add" />
                                                </span>
                                            </Link>
                                            <input
                                                type="file"
                                                id="folder-input"
                                                className="file-input"
                                                style={{ display: 'none' }}
                                                ref={folderInputRef}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-form">
                            <div className="row">
                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label className="form-label">Card Holder Name</label>
                                        <div className="user-icon">
                                            <span className="input-icon fs-14">
                                                <i className="isax isax-user" />
                                            </span>
                                            <input type="text" className="form-control " />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label className="form-label">Card Number</label>
                                        <div className="user-icon">
                                            <span className="input-icon fs-14">
                                                <i className="isax isax-card-tick" />
                                            </span>
                                            <input type="text" className="form-control " />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label className="form-label">Expire Date</label>
                                        <div className="user-icon">
                                            <span className="input-icon fs-14">
                                                <i className="isax isax-calendar-2" />
                                            </span>
                                            <DatePicker
                                                className="form-control datetimepicker"
                                                placeholder="dd/mm/yyyy"
                                                format="DD-MM-YYYY"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label className="form-label">CVV</label>
                                        <div className="user-icon">
                                            <span className="input-icon fs-14">
                                                <i className="isax isax-check" />
                                            </span>
                                            <input type="text" className="form-control " />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Credit Card */}
                    {/* Paypal */}
                    <div className="paypal-details" style={{ display: paymentRadio === 'paypal' ? 'block' : 'none' }}>
                        <div className="mb-3">
                            <h6 className="fs-16 ">Payment With Paypal</h6>
                        </div>
                        <div className="row">
                            <div className="col-lg-6">
                                <div className="mb-3">
                                    <label className="form-label">Email Address</label>
                                    <div className="user-icon">
                                        <span className="input-icon fs-14">
                                            <i className="isax isax-sms" />
                                        </span>
                                        <input type="email" className="form-control " />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="mb-3">
                                    <label className="form-label">Password</label>
                                    <div className="input-icon">
                                        <span className="input-icon-addon">
                                            <i className="isax isax-lock" />
                                        </span>
                                        <input
                                            type={
                                                passwordVisibility.password
                                                    ? "text"
                                                    : "password"
                                            }
                                            className="pass-input form-control"
                                        />
                                        <span
                                            className={`isax toggle-passwords ${passwordVisibility.password
                                                ? "isax-eye"
                                                : "isax-eye-slash"
                                                }`}
                                            onClick={() =>
                                                togglePasswordVisibility("password")
                                            }
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Paypal */}
                    {/* Stripe */}
                    <div className="stripe-details" style={{ display: paymentRadio === 'stripe' ? 'block' : 'none' }}>
                        <div className="mb-3">
                            <h6 className="fs-16">Payment With Stripe</h6>
                        </div>
                        <div className="row">
                            <div className="col-lg-6">
                                <div className="mb-3">
                                    <label className="form-label">Email Address</label>
                                    <div className="user-icon">
                                        <span className="input-icon fs-14">
                                            <i className="isax isax-sms" />
                                        </span>
                                        <input type="email" className="form-control " />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="mb-3">
                                    <label className="form-label">Password</label>
                                    <div className="input-icon">
                                        <span className="input-icon-addon">
                                            <i className="isax isax-lock" />
                                        </span>
                                        <input
                                            type={
                                                passwordVisibility.password
                                                    ? "text"
                                                    : "password"
                                            }
                                            className="pass-input form-control"
                                        />
                                        <span
                                            className={`isax toggle-passwords ${passwordVisibility.password
                                                ? "isax-eye"
                                                : "isax-eye-slash"
                                                }`}
                                            onClick={() =>
                                                togglePasswordVisibility("password")
                                            }
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Stripe */}
                    <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
                        <Link
                            to="#"
                            className="btn btn-primary"
                            onClick={() => setAddModal(true)}
                        >
                            Confirm &amp; Pay $9569{" "}
                        </Link>
                    </div>
                </div>
            </div>
            <PaymentModal setAddModal={setAddModal}
                title={location.pathname.includes('cruise') ? 'Coral Cruiser - Balcony' :
                    location.pathname.includes('tour') ? 'Rainbow Mountain Valley' :
                        location.pathname.includes('flight') ? 'Econnomy Class' :
                            location.pathname.includes('car') ? 'Audi A3 2019' : 'Queen Room'}
                link={location.pathname.includes('cruise') ? all_routes.cruiseBookingConfirmation :
                    location.pathname.includes('tour') ? all_routes.tourBookingConfirmation :
                        location.pathname.includes('flight') ? all_routes.flightBookingConfirmation :
                            location.pathname.includes('car') ? all_routes.carBookingConfirmation : all_routes.hotelBookingConfirmation}
                addModal={addModal} />
            {/* Checkout */}
        </>

    )
}

export default BookingForm
